/**
 * A ticket's header actions (TMMPDM-0109; TMMAPI-0181 item 30), each a confirmed server action that
 * tmm-api audits. Change status offers that type's statuses (the server decides which transition is
 * allowed: 409 `TICKET_TRANSITION_INVALID {current}` is worded "That status can't follow …"); Mark as
 * duplicate picks the canonical feature request by number or title (422 `TICKET_DUPLICATE_INVALID`
 * in the contract's words); Set "shipped in" takes free text up to 200 characters, and empty clears
 * it. Errors appear in the dialog. On success the page re-reads the ticket.
 */
import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isValidationFailed, ticketTransitionCurrent } from '@/api/errors';
import type { TicketRow, TicketView } from '@/api/tmm/types';
import {
  isNotDeployed,
  notAvailableText,
  parseTicketNumber,
  statusLabel,
  statusesOf,
  ticketRef,
  type Translate,
} from './tickets';

/** A failed action in words: not deployed, the code's copy, or the dialog's own line. */
function actionError(error: unknown, t: Translate, fallback: string): string {
  if (isNotDeployed(error)) return notAvailableText(t);
  return errorCopy(error, t, fallback);
}

interface ActionDialogProps {
  open: boolean;
  ticket: TicketView;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Change status…: that type's statuses but the current one. `DUPLICATE` is reached by Mark as
 * duplicate, which names the canonical ticket, so it isn't offered here.
 */
export function ChangeStatusDialog({ open, ticket, onClose, onDone }: ActionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="ticket-status">
      {open ? <ChangeStatusForm ticket={ticket} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}

function ChangeStatusForm({ ticket, onClose, onDone }: Omit<ActionDialogProps, 'open'>) {
  const { t } = useCopy();
  const options = statusesOf(ticket.type).filter(
    (status) => status !== ticket.status && status !== 'DUPLICATE',
  );
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const current = statusLabel(ticket.status, t);

  const submit = async () => {
    if (status === '') {
      setFieldError(t('tickets.changeStatus.required', 'Choose a status.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApi.changeTicketStatus(ticket.number, { status });
      onDone();
    } catch (failure: unknown) {
      // TMMPDM-0110 (0181 item 30 at `56a8fa38`): 409 TICKET_TRANSITION_INVALID names the status the
      // ticket has now; without one, the status this page shows.
      const from = ticketTransitionCurrent(failure);
      setError(
        from !== null
          ? t('tickets.changeStatus.conflict', "That status can't follow {{status}}.", {
              status: from === '' ? current : statusLabel(from, t),
            })
          : actionError(failure, t, t('tickets.changeStatus.error', "Couldn't change the status.")),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle id="ticket-status">
        {t('tickets.changeStatus.title', 'Change the status of {{ticket}}', {
          ticket: ticketRef(ticket.type, ticket.number),
        })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('tickets.changeStatus.current', 'Now: {{status}}', { status: current })}
          </Typography>
          <TextField
            select
            label={t('tickets.changeStatus.new', 'New status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setFieldError(null);
              setError(null);
            }}
            error={fieldError !== null}
            helperText={fieldError ?? ' '}
          >
            {options.map((code) => (
              <MenuItem key={code} value={code}>
                {statusLabel(code, t)}
              </MenuItem>
            ))}
          </TextField>
          <Alert severity="warning">
            {t('tickets.changeStatus.emailed', 'The member is emailed about this change.')}
          </Alert>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('tickets.changeStatus.confirm', 'Change status')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

/** Mark as duplicate… (feature requests): the canonical ticket, searched by number or title. */
export function MarkDuplicateDialog({ open, ticket, onClose, onDone }: ActionDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="ticket-duplicate"
    >
      {open ? <MarkDuplicateForm ticket={ticket} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}

const SEARCH_SIZE = 10;

function MarkDuplicateForm({ ticket, onClose, onDone }: Omit<ActionDialogProps, 'open'>) {
  const { t } = useCopy();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<TicketRow[]>([]);
  const [selected, setSelected] = useState<TicketRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const ref = ticketRef(ticket.type, ticket.number);

  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(input.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [input]);

  useEffect(() => {
    let cancelled = false;
    // A typed number is read directly too: the text search may not match numbers.
    const number = query === '' ? null : parseTicketNumber(query);
    Promise.all([
      adminApi
        .listTickets({
          type: ticket.type,
          ...(query !== '' && number === null ? { q: query } : {}),
          page: 0,
          size: SEARCH_SIZE,
        })
        .then((page) => page.tickets)
        .catch(() => [] as TicketRow[]),
      number === null
        ? Promise.resolve(null)
        : adminApi.getTicket(number).catch(() => null as TicketView | null),
    ]).then(([rows, direct]) => {
      if (cancelled) return;
      const found = direct !== null && direct.type === ticket.type ? [direct, ...rows] : rows;
      const seen = new Set<number>();
      setOptions(
        found.filter((row) => {
          // Itself, a repeat, or a ticket that is itself a duplicate (that would build a chain).
          if (row.number === ticket.number || seen.has(row.number)) return false;
          if (row.duplicateOf !== undefined) return false;
          seen.add(row.number);
          return true;
        }),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [query, ticket.number, ticket.type]);

  const submit = async () => {
    if (selected === null) {
      setFieldError(t('tickets.duplicate.required', 'Choose the ticket it duplicates.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await adminApi.markTicketDuplicate(ticket.number, { duplicateOf: selected.number });
      onDone();
    } catch (failure: unknown) {
      setError(
        actionError(failure, t, t('tickets.duplicate.error', "Couldn't mark it as a duplicate.")),
      );
    } finally {
      setBusy(false);
    }
  };

  const optionLabel = (row: TicketRow) => `${ticketRef(row.type, row.number)} · ${row.title}`;

  return (
    <>
      <DialogTitle id="ticket-duplicate">
        {t('tickets.duplicate.title', 'Mark {{ticket}} as a duplicate', { ticket: ref })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={options}
            value={selected}
            onChange={(_event, next) => {
              setSelected(next);
              setFieldError(null);
              setError(null);
            }}
            onInputChange={(_event, next, reason) => {
              if (reason === 'input') setInput(next);
              if (reason === 'clear') setInput('');
            }}
            filterOptions={(all) => all}
            getOptionLabel={optionLabel}
            getOptionKey={(row) => row.number}
            isOptionEqualToValue={(a, b) => a.number === b.number}
            noOptionsText={t('tickets.duplicate.noOptions', 'No matching tickets')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('tickets.duplicate.canonical', 'Duplicate of')}
                helperText={fieldError ?? t('tickets.duplicate.hint', 'Search by number or title')}
                error={fieldError !== null}
              />
            )}
          />
          {selected !== null ? (
            <DialogContentText>
              {t(
                'tickets.duplicate.consequence',
                '{{ticket}} will point to {{canonical}}; its requester follows that ticket.',
                { ticket: ref, canonical: ticketRef(selected.type, selected.number) },
              )}
            </DialogContentText>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('tickets.duplicate.confirm', 'Mark as duplicate')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

const SHIPPED_IN_MAX = 200;

/** Set "shipped in"… (feature requests): free text; empty clears it. */
export function ShippedInDialog({ open, ticket, onClose, onDone }: ActionDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="ticket-shipped">
      {open ? <ShippedInForm ticket={ticket} onClose={onClose} onDone={onDone} /> : null}
    </Dialog>
  );
}

function ShippedInForm({ ticket, onClose, onDone }: Omit<ActionDialogProps, 'open'>) {
  const { t } = useCopy();
  const fieldId = useId();
  const [value, setValue] = useState(ticket.shippedIn ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setFieldError(null);
    try {
      await adminApi.setTicketShippedIn(ticket.number, { shippedIn: value.trim() });
      onDone();
    } catch (failure: unknown) {
      if (isValidationFailed(failure)) {
        setFieldError(
          t('errors.field.rejected', "This value isn't accepted. Check it and try again."),
        );
      } else {
        setError(
          actionError(failure, t, t('tickets.shippedIn.error', 'Couldn\'t save "shipped in".')),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle id="ticket-shipped">
        {t('tickets.shippedIn.title', 'Set "shipped in" for {{ticket}}', {
          ticket: ticketRef(ticket.type, ticket.number),
        })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <TextField
            id={fieldId}
            fullWidth
            label={t('tickets.shippedIn.label', 'Shipped in')}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setFieldError(null);
            }}
            error={fieldError !== null}
            helperText={
              fieldError ??
              t(
                'tickets.shippedIn.hint',
                'A release or version, as free text. Leave empty to clear it.',
              )
            }
            // TMMAPI-0181 item 30: at most 200 characters, trimmed by the server.
            slotProps={{ htmlInput: { maxLength: SHIPPED_IN_MAX } }}
          />
        </Box>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('tickets.shippedIn.confirm', 'Save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
